import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { 
  Calendar, 
  Users, 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  BarChart3,
  FileText,
  Search,
  Download,
  Eye,
  X,
  MoreVertical,
  UserCheck,
  HelpCircle,
  LogOut  // Add this!
} from 'lucide-react';

const ResearcherDashboard = () => {
  console.log('🚨 RESEARCHER DASHBOARD LOADED - VERSION 4.0 🚨');
  
  // Main States
  const [activeTab, setActiveTab] = useState('participants');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  
  // Data States
  const [participants, setParticipants] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetails, setParticipantDetails] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [participantCategory, setParticipantCategory] = useState('active');
  
  // Assessment Links States
  const [selectedSession, setSelectedSession] = useState(1);
  const [searchAssessmentEmail, setSearchAssessmentEmail] = useState('');
  const [selectedAssessmentParticipant, setSelectedAssessmentParticipant] = useState(null);
  const [editingLinks, setEditingLinks] = useState({
    preSessionUrl: ''
  });
  const [participantLinks, setParticipantLinks] = useState([]);
  const [savingLinks, setSavingLinks] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksSaveMessage, setLinksSaveMessage] = useState('');
  const [linksErrorMessage, setLinksErrorMessage] = useState('');
  
  // Session Scheduling States
  const [schedulingParticipant, setSchedulingParticipant] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('08:00'); 
  const [scheduleNotes, setScheduleNotes] = useState('');
  const [schedulingData, setSchedulingData] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  
  // Participant Categories
const [participantCategories, setParticipantCategories] = useState({
  test: [],
  active: [],
  pending: [],
  admin: [],
  completed: []
});
  
  const [openDropdown, setOpenDropdown] = useState(null);
  
// Stats
  const [stats, setStats] = useState({
    totalParticipants: 0,
    activeParticipants: 0,
    completedProgram: 0,
    totalPracticeLogs: 0,
    assessmentCompletionRate: 0
  });

  // Create User States
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
  email: '',
  password: '',
  patientId: '',
  notes: '',
  role: 'patient'
});
  const [creatingUser, setCreatingUser] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [helpSearchTerm, setHelpSearchTerm] = useState('');
  const [expandedHelpTopic, setExpandedHelpTopic] = useState(null);



 useEffect(() => {
  loadDashboardData();
  loadParticipantCategories();
}, []);

  useEffect(() => {
    if (activeTab === 'assessment-links') {
      loadConfiguredParticipants();
    } else if (activeTab === 'session-scheduling') {
      loadSessionSchedules();
    }
  }, [activeTab, selectedSession]);

const loadDashboardData = async () => {
  try {
    setLoading(true);
    setError(''); // Clear any previous errors
    console.log('=== STARTING DASHBOARD DATA LOAD ===');
    
    // Step 1: Fetch users
    console.log('Step 1: Fetching users...');
    const { data: users, error: userError } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    console.log('Users response:', { count: users?.length, error: userError });
    
    if (userError) {
      console.error('❌ User fetch error:', userError);
      throw new Error(`Failed to fetch users: ${userError.message}`);
    }

    if (!users || users.length === 0) {
      console.warn('⚠️ No users found');
      setParticipants([]);
      setAllParticipants([]);
      setStats({
        totalParticipants: 0,
        activeParticipants: 0,
        completedProgram: 0,
        totalPracticeLogs: 0,
        assessmentCompletionRate: 0
      });
      setLoading(false);
      return;
    }

    console.log(`✅ Found ${users.length} users`);
    const patientUsers = users || [];
    setAllParticipants(patientUsers);

    // Step 2: Enrich user data
    console.log('Step 2: Enriching user data...');
    const enrichedUsers = await Promise.all(patientUsers.map(async (user, index) => {
      console.log(`  Processing user ${index + 1}/${patientUsers.length}: ${user.email}`);
      
      try {
        const { data: sessions, error: sessionsError } = await supabase
          .from('user_sessions')
          .select('*')
          .eq('user_id', user.id)
          .order('session_number');
        
        if (sessionsError) {
          console.error(`    ❌ Sessions error for ${user.email}:`, sessionsError);
        }
        
        const { data: practiceLogs, error: logsError } = await supabase
          .from('practice_logs')
          .select('*')
          .eq('user_id', user.id);
        
        if (logsError) {
          console.error(`    ❌ Practice logs error for ${user.email}:`, logsError);
        }
        
        const { data: assessments, error: assessmentsError } = await supabase
          .from('participant_assessments')
          .select('*')
          .eq('user_id', user.id);
        
        if (assessmentsError) {
          console.error(`    ❌ Assessments error for ${user.email}:`, assessmentsError);
        }
        
        const { data: schedule, error: scheduleError } = await supabase
          .from('participant_session_schedule')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (scheduleError) {
          console.error(`    ❌ Schedule error for ${user.email}:`, scheduleError);
        }

        return {
          ...user,
          user_sessions: sessions || [],
          practice_logs: practiceLogs || [],
          participant_assessments: assessments || [],
          participant_session_schedule: schedule
        };
      } catch (err) {
        console.error(`    ❌ Error processing ${user.email}:`, err);
        return {
          ...user,
          user_sessions: [],
          practice_logs: [],
          participant_assessments: [],
          participant_session_schedule: null
        };
      }
    }));

    console.log('✅ User data enriched');

    // Step 3: Process participants (your existing code continues...)
    console.log('Step 3: Processing participant data...');
    const processedParticipants = enrichedUsers.map(user => {
      const sessions = user.user_sessions || [];
      // Exclude intro session (session 0) from counts
      const realSessions = sessions.filter((s) => s.session_number > 0);
      const completedSessions = realSessions.filter(
        (s) =>
          s.session_completed === true || s.session_fully_completed === true,
      ).length;

      const totalSessions = 5;
      const progress =
        realSessions.length > 0 ? (completedSessions / totalSessions) * 100 : 0;

      let lastActivity = new Date(user.updated_at || user.created_at);

      if (realSessions.length > 0) {
        const sessionActivity = realSessions.reduce((latest, session) => {
          const sessionDate = new Date(session.updated_at);
          return sessionDate > latest ? sessionDate : latest;
        }, new Date(0));

        if (sessionActivity > lastActivity) {
          lastActivity = sessionActivity;
        }
      }

      const totalPracticeMinutes =
        user.practice_logs?.reduce((sum, log) => sum + (log.minutes || 0), 0) ||
        0;

      const assessmentsBySession = {};
      realSessions.forEach((session) => {
        assessmentsBySession[session.session_number] = {
          pre: session.pre_assessment_completed === true,
        };
      });

      const completedPreAssessments = realSessions.filter(
        (s) => s.pre_assessment_completed === true,
      ).length;
      //const completedPostAssessments = realSessions.filter(s => s.post_assessment_completed === true).length;
      const totalExpectedAssessments = realSessions.length;
      // Session 1 doesn't have pre-assessment, so total pre-assessments is sessions - 1 (if session 1 exists)
      const totalExpectedPreAssessments = realSessions.some(
        (s) => s.session_number === 1,
      )
        ? totalExpectedAssessments - 1
        : totalExpectedAssessments;

      return {
        ...user,
        progress,
        completedSessions,
        totalSessions,
        lastActivity,
        daysSinceLastActivity: Math.floor(
          (new Date() - lastActivity) / (1000 * 60 * 60 * 24),
        ),
        totalPracticeMinutes,
        practiceLogCount: user.practice_logs?.length || 0,
        assessmentsBySession,
        completedPreAssessments,
        //completedPostAssessments,
        totalExpectedAssessments,
        totalExpectedPreAssessments,
        hasSchedule: !!user.participant_session_schedule,
        sessionsStarted: realSessions.filter((s) => s.session_started === true)
          .length,
        videosWatched: realSessions.filter(
          (s) => s.video_watched === true || s.video_completed === true,
        ).length,
        quizzesCompleted: realSessions.filter((s) => s.quiz_completed === true)
          .length,
        rawSessions: sessions,
      };
    });

    setParticipants(processedParticipants);
    console.log(`✅ Processed ${processedParticipants.length} participants`);
    
    // Calculate stats
    console.log('Step 4: Calculating stats...');
    const nonTestParticipants = processedParticipants.filter(p => !participantCategories.test.includes(p.id));
    const activeCount = nonTestParticipants.filter(p => p.daysSinceLastActivity <= 7).length;
    const completedCount = nonTestParticipants.filter(p => p.progress === 100).length;
    const totalPractice = nonTestParticipants.reduce((sum, p) => sum + p.practiceLogCount, 0);
    
    const totalAssessmentsExpected = nonTestParticipants.reduce(
      (sum, p) => sum + p.totalExpectedPreAssessments,
      0,
    );
    const totalAssessmentsCompleted = nonTestParticipants.reduce(
      (sum, p) => sum + p.completedPreAssessments,
      0,
    );
    const assessmentRate = totalAssessmentsExpected > 0 
      ? (totalAssessmentsCompleted / totalAssessmentsExpected) * 100 
      : 0;
    
    setStats({
      totalParticipants: nonTestParticipants.length,
      activeParticipants: activeCount,
      completedProgram: completedCount,
      totalPracticeLogs: totalPractice,
      assessmentCompletionRate: assessmentRate
    });
    
    console.log('✅ Stats calculated:', {
      totalParticipants: nonTestParticipants.length,
      activeParticipants: activeCount,
      completedProgram: completedCount
    });
    
    console.log('=== DASHBOARD DATA LOAD COMPLETE ===');
    
  } catch (error) {
    console.error('❌ FATAL ERROR in loadDashboardData:', error);
    setError(`Failed to load dashboard: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const loadParticipantCategories = async () => {
  try {
    const { data: categories, error } = await supabase
      .from('participant_categories')
      .select('user_id, category');

    if (error && error.code !== 'PGRST116') throw error;

    const categorized = {
      test: [],
      active: [],
      pending: [],
      admin: [],
      completed: []
    };

    if (categories && categories.length > 0) {
      categories.forEach(cat => {
        if (categorized[cat.category]) {
          categorized[cat.category].push(cat.user_id);
        }
      });
    }

    setParticipantCategories(categorized);
  } catch (error) {
    console.error('Error loading participant categories:', error);
  }
};

const moveParticipantToCategory = async (participantId, newCategory) => {
  try {
    if (newCategory === 'active') {
      // Remove from categories table (active is default)
      const { error } = await supabase
        .from('participant_categories')
        .delete()
        .eq('user_id', participantId);

      if (error) throw error;
    } else {
      // Upsert to categories table
      const { error } = await supabase
        .from('participant_categories')
        .upsert({
          user_id: participantId,
          category: newCategory,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    }

    // Reload categories
    await loadParticipantCategories();
    setOpenDropdown(null);

  } catch (error) {
    console.error('Error updating participant category:', error);
    setError('Error updating category: ' + error.message);
  }
};

const getParticipantCategory = (participant) => {
  // Auto-categorize admins and researchers
  if (participant.role === 'admin' || participant.role === 'researcher') return 'admin';
  
  // Check manual categories
  if (participantCategories.test.includes(participant.id)) return 'test';
  if (participantCategories.pending.includes(participant.id)) return 'pending';
  if (participantCategories.completed.includes(participant.id)) return 'completed';
  
  // Auto-complete if 100% progress
  if (participant.progress === 100) return 'completed';
  
  return 'active';
};

const loadParticipantDetails = async (userId) => {
  try {
    console.log('🔍 Loading details for user:', userId);
    
    // Fetch user data with existing relationships
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        user_sessions (*),
        practice_logs (*),
        participant_assessments (*),
        participant_session_schedule (*)
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;
    
    // Fetch manual overrides separately
    const { data: overrides, error: overridesError } = await supabase
      .from('manual_session_overrides')
      .select('*')
      .eq('user_id', userId);
    
    if (overridesError) {
      console.error('Error loading overrides:', overridesError);
    }
    
    // Combine the data
    const combinedData = {
      ...data,
      manual_session_overrides: overrides || []
    };
    
    console.log('✅ Loaded participant details:', combinedData);
    
    setParticipantDetails(combinedData);
    setSelectedParticipant(userId);
    
  } catch (error) {
    console.error('Error loading participant details:', error);
    setError('Failed to load participant details: ' + error.message);
  }
};

  const exportData = () => {
    const exportParticipants = participants.filter(p => !participantCategories.test.includes(p.id));
    const headers = [
      'Email',
      'Progress',
      'Sessions Completed',
      'Last Activity',
      'Practice Minutes',
      'Pre-Assessment',
    ];
    const rows = exportParticipants.map((p) => [
      p.email,
      `${p.progress.toFixed(1)}%`,
      `${p.completedSessions}/${p.totalSessions}`,
      p.lastActivity.toLocaleDateString(),
      p.totalPracticeMinutes,
      `${p.completedPreAssessments}/${p.totalExpectedPreAssessments}`,
      //`${p.completedPostAssessments}/${p.totalExpectedAssessments}`
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `participant-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (daysInactive) => {
    if (daysInactive <= 3) return '#10B981';
    if (daysInactive <= 7) return '#F59E0B';
    return '#EF4444';
  };

  const getStatusLabel = (daysInactive) => {
    if (daysInactive <= 3) return 'Active';
    if (daysInactive <= 7) return 'Low Activity';
    return 'Inactive';
  };

  const toLocalDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const minScheduleDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return toLocalDateInputValue(date);
  })();

  // Assessment Links Functions
  const loadConfiguredParticipants = async () => {
    try {
      setLoadingLinks(true);
      const { data: assessments, error } = await supabase
        .from('participant_assessments')
        .select('*')
        .eq('session_number', selectedSession)
        .order('created_at', { ascending: false });

      if (error && error.code !== 'PGRST116') throw error;

      const configuredData = [];
      
      if (assessments && assessments.length > 0) {
        for (const assessment of assessments) {
          if (assessment.user_id) {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('email, created_at')
              .eq('id', assessment.user_id)
              .single();

            if (!userError && userData) {
              configuredData.push({
                id: assessment.user_id,
                email: assessment.participant_email || userData.email,
                participantId: assessment.participant_id,
                preSessionUrl: assessment.pre_assessment_url || '',
                assessmentId: assessment.id,
                createdAt: userData.created_at,
                lastUpdated: assessment.updated_at,
                sessionNumber: assessment.session_number
              });
            }
          }
        }
      }

      setParticipantLinks(configuredData);
      
    } catch (error) {
      console.error('Error loading configured participants:', error);
      setError('Error loading assessment data: ' + error.message);
    } finally {
      setLoadingLinks(false);
    }
  };

  const searchAssessmentParticipants = () => {
    if (!searchAssessmentEmail.trim()) return allParticipants;
    return allParticipants.filter(participant => 
      participant.email && participant.email.toLowerCase().includes(searchAssessmentEmail.toLowerCase())
    );
  };

  const selectAssessmentParticipant = (participant) => {
    if (!participant || !participant.id || !participant.email) {
      setLinksErrorMessage('Invalid participant data. Please try selecting again.');
      setError('Invalid participant data. Please try selecting again.');
      return;
    }
    
    setSelectedAssessmentParticipant(participant);
    setLinksSaveMessage('');
    setLinksErrorMessage('');
    const existingConfig = participantLinks.find(p => p.id === participant.id);
    
    if (existingConfig) {
      setEditingLinks({
        preSessionUrl: existingConfig.preSessionUrl || ''
      });
    } else {
      setEditingLinks({
        preSessionUrl: ''
      });
    }
  };

  const saveParticipantLinks = async () => {
    if (!selectedAssessmentParticipant || !selectedAssessmentParticipant.id) {
      setLinksErrorMessage('No participant selected');
      setError('No participant selected');
      return;
    }
    
    const trimmedPreSessionUrl = (editingLinks.preSessionUrl || '').trim();

    if (!trimmedPreSessionUrl) {
      setLinksErrorMessage('Please enter a pre-session assessment URL');
      setError('Please enter a pre-session assessment URL');
      return;
    }
    
    try {
      setSavingLinks(true);
      setError('');
      setLinksSaveMessage('');
      setLinksErrorMessage('');

      const { data: existingAssessment, error: lookupError } = await supabase
        .from('participant_assessments')
        .select('id')
        .eq('user_id', selectedAssessmentParticipant.id)
        .eq('session_number', selectedSession)
        .maybeSingle();

      if (lookupError) throw lookupError;

      if (existingAssessment?.id) {
        const { error } = await supabase
          .from('participant_assessments')
          .update({
            pre_assessment_url: trimmedPreSessionUrl,
            post_assessment_url: '',
            participant_email: selectedAssessmentParticipant.email,
            session_number: selectedSession,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingAssessment.id);

        if (error) throw error;
      } else {
        const participantId = selectedAssessmentParticipant.email + '_S' + selectedSession;
        const insertPayload = {
          user_id: selectedAssessmentParticipant.id,
          participant_id: participantId,
          participant_email: selectedAssessmentParticipant.email,
          participant_name: selectedAssessmentParticipant.email.split('@')[0],
          session_number: selectedSession,
          pre_assessment_url: trimmedPreSessionUrl,
          post_assessment_url: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        const { error: insertError } = await supabase
          .from('participant_assessments')
          .insert(insertPayload);

        if (insertError) {
          const isUserScopedConstraint =
            insertError.code === '23505' ||
            /duplicate key|unique/i.test(insertError.message || '');

          if (isUserScopedConstraint) {
            throw new Error(
              'Could not save this session link. Please refresh and try again.',
            );
          }

          throw insertError;
        }
      }
      
      setLinksSaveMessage('Assessment links saved successfully.');
      setLinksErrorMessage('');
      await loadConfiguredParticipants();
      setSelectedAssessmentParticipant(null);
      setEditingLinks({ preSessionUrl: '' });
      setError('');
      
    } catch (error) {
      console.error('Error saving assessment links:', error);
      const saveMessage = 'Unable to save assessment links: ' + (error.message || 'Unknown error');
      setError('Error saving links: ' + error.message);
      setLinksErrorMessage(saveMessage);
    } finally {
      setSavingLinks(false);
    }
  };

  const removeParticipantLinks = async (participantId, email) => {
    if (!confirm('Remove assessment links for ' + email + '?')) return;
    
    try {
      const { error } = await supabase
        .from('participant_assessments')
        .delete()
        .eq('user_id', participantId)
        .eq('session_number', selectedSession);

      if (error) throw error;
      alert('Assessment links removed');
      await loadConfiguredParticipants();
      
    } catch (error) {
      console.error('Error removing participant links:', error);
      setError('Error removing links: ' + error.message);
    }
  };

  // Session Scheduling Functions
  const loadSessionSchedules = async () => {
    try {
      setLoadingSchedule(true);
      const { data: schedules, error } = await supabase
        .from('participant_session_schedule')
        .select('*')
        .order('session_1_start_date', { ascending: true });

      if (error && error.code !== 'PGRST116') throw error;

      const scheduleData = [];
      
      if (schedules && schedules.length > 0) {
        for (const schedule of schedules) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('email')
            .eq('id', schedule.user_id)
            .single();

          if (!userError && userData) {
            scheduleData.push({
              id: schedule.user_id,
              email: userData.email,
              startDate: schedule.session_1_start_date,
              session1Unlock: schedule.session_1_unlock_time,
              session2Unlock: schedule.session_2_unlock_time,
              session3Unlock: schedule.session_3_unlock_time,
              session4Unlock: schedule.session_4_unlock_time,
              session5Unlock: schedule.session_5_unlock_time,
              scheduleId: schedule.id,
              notes: schedule.notes
            });
          }
        }
      }

      setSchedulingData(scheduleData);
      
    } catch (error) {
      console.error('Error loading session schedules:', error);
      setError('Error loading session schedules: ' + error.message);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const saveSessionSchedule = async () => {
  if (!schedulingParticipant || !startDate || !startTime) {
    setError('Please select a participant, start date, and start time');
    return;
  }
    
    try {
      setSavingSchedule(true);
      const participant = allParticipants.find(p => p.id === schedulingParticipant);
      if (!participant) return;
      
      const startDateTime = `${startDate}T${startTime}:00`;

      const { data: unlockTimes, error: calcError } = await supabase
        .rpc('calculate_session_unlock_times', { 
          start_date: startDateTime,
          participant_timezone: 'UTC'
        });

      if (calcError) throw calcError;

      const times = unlockTimes[0];
      const existingSchedule = schedulingData.find(s => s.id === schedulingParticipant);
      
      if (existingSchedule) {
        const { error } = await supabase
          .from('participant_session_schedule')
          .update({
            session_1_start_date: startDate,
            session_1_unlock_time: times.session_1_unlock,
            session_2_unlock_time: times.session_2_unlock,
            session_3_unlock_time: times.session_3_unlock,
            session_4_unlock_time: times.session_4_unlock,
            session_5_unlock_time: times.session_5_unlock,
            notes: scheduleNotes,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSchedule.scheduleId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('participant_session_schedule')
          .insert({
            user_id: schedulingParticipant,
            participant_email: participant.email,
            session_1_start_date: startDate,
            session_1_unlock_time: times.session_1_unlock,
            session_2_unlock_time: times.session_2_unlock,
            session_3_unlock_time: times.session_3_unlock,
            session_4_unlock_time: times.session_4_unlock,
            session_5_unlock_time: times.session_5_unlock,
            notes: scheduleNotes,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }
      
      alert('Session schedule saved!');
      await loadSessionSchedules();
      await loadDashboardData();
      setSchedulingParticipant('');
      setStartDate('');
      setStartTime('08:00');
      setScheduleNotes('');
      
    } catch (error) {
      console.error('Error saving session schedule:', error);
      setError('Error saving schedule: ' + error.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  const removeSessionSchedule = async (participantId, email) => {
    if (!confirm('Remove session schedule for ' + email + '?')) return;
    
    try {
      const { error } = await supabase
        .from('participant_session_schedule')
        .delete()
        .eq('user_id', participantId);

      if (error) throw error;
      alert('Session schedule removed');
      await loadSessionSchedules();
      await loadDashboardData();
      
    } catch (error) {
      console.error('Error removing session schedule:', error);
      setError('Error removing schedule: ' + error.message);
    }
  };

  const getSessionStatus = (unlockTime) => {
    const now = new Date();
    const unlock = new Date(unlockTime);
    return now >= unlock ? 'Unlocked' : 'Locked';
  };

  const getSessionColor = (unlockTime) => {
    const now = new Date();
    const unlock = new Date(unlockTime);
    return now >= unlock ? '#059669' : '#DC2626';
  };

     const handleViewAsUser = (participant) => {
    sessionStorage.setItem('impersonating_user_id', participant.id);
    sessionStorage.setItem('impersonating_user_email', participant.email);
    sessionStorage.setItem('is_admin_view', 'true'); // Mark this as admin viewing
    window.location.href = '/dashboard';
  };

  const handleViewParticipantDashboard = () => {
    sessionStorage.removeItem('impersonating_user_id');
    sessionStorage.removeItem('impersonating_user_email');
    window.location.href = '/dashboard';
  };

  const getWeekStart = (date) => {
    const weekStart = new Date(date);
    weekStart.setHours(0, 0, 0, 0);
    const day = weekStart.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diffToMonday);
    return weekStart;
  };

  const getWeeklyPracticeCounts = (practiceLogs = []) => {
    const now = new Date();
    const currentWeekStart = getWeekStart(now);
    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(currentWeekStart.getDate() + 7);

    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(currentWeekStart.getDate() - 7);

    let currentWeekCount = 0;
    let lastWeekCount = 0;

    practiceLogs.forEach((log) => {
      const logDateRaw = log.practice_date || log.created_at;
      const logDate = new Date(logDateRaw);

      if (Number.isNaN(logDate.getTime())) {
        return;
      }

      if (logDate >= currentWeekStart && logDate < nextWeekStart) {
        currentWeekCount += 1;
      } else if (logDate >= lastWeekStart && logDate < currentWeekStart) {
        lastWeekCount += 1;
      }
    });

    return {
      currentWeekCount,
      lastWeekCount,
    };
  };

  const weeklyPracticeCounts = getWeeklyPracticeCounts(participantDetails?.practice_logs || []);


const filteredParticipants = participants.filter(p => {
    const category = getParticipantCategory(p);
    if (participantCategory !== category) return false;
    
    const matchesSearch = p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.patient_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (filterStatus === 'active') matchesStatus = p.daysSinceLastActivity <= 7;
    else if (filterStatus === 'inactive') matchesStatus = p.daysSinceLastActivity > 7;
    else if (filterStatus === 'completed') matchesStatus = p.progress === 100;
    
    return matchesSearch && matchesStatus;
  });

const categoryCounts = {
  active: participants.filter(p => getParticipantCategory(p) === 'active').length,
  completed: participants.filter(p => getParticipantCategory(p) === 'completed').length,
  test: participants.filter(p => getParticipantCategory(p) === 'test').length,
  pending: participants.filter(p => getParticipantCategory(p) === 'pending').length,
  admin: participants.filter(p => getParticipantCategory(p) === 'admin').length
};

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#F9FAFB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E5E7EB',
            borderTop: '4px solid #1B365D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6B7280' }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#F9FAFB',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Participant Detail Modal */}
      {selectedParticipant && participantDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '24px',
                borderBottom: '1px solid #E5E7EB',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 10,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    margin: '0 0 4px 0',
                  }}
                >
                  {participantDetails.email}
                </h2>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  ID:{' '}
                  {participantDetails.patient_id ||
                    participantDetails.id.substring(0, 8)}
                </p>
              </div>

              <div
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                {/* Admin Toggle */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      color: '#6B7280',
                      fontWeight: '500',
                    }}
                  >
                    Admin
                  </span>
                  <label
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '44px',
                      height: '24px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        participantDetails.role === 'admin' ||
                        participantDetails.role === 'researcher'
                      }
                      onChange={async (e) => {
                        const newRole = e.target.checked ? 'admin' : 'patient';
                        try {
                          const { error } = await supabase
                            .from('users')
                            .update({ role: newRole })
                            .eq('id', participantDetails.id);

                          if (error) throw error;

                          setParticipantDetails({
                            ...participantDetails,
                            role: newRole,
                          });
                          await loadDashboardData();
                          alert(`User role updated to ${newRole}`);
                        } catch (error) {
                          console.error('Error updating role:', error);
                          alert('Error updating user role: ' + error.message);
                        }
                      }}
                      style={{
                        opacity: 0,
                        width: 0,
                        height: 0,
                        position: 'absolute',
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor:
                          participantDetails.role === 'admin' ||
                          participantDetails.role === 'researcher'
                            ? '#10B981'
                            : '#D1D5DB',
                        transition: 'background-color 0.2s',
                        borderRadius: '24px',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          content: '',
                          height: '18px',
                          width: '18px',
                          left:
                            participantDetails.role === 'admin' ||
                            participantDetails.role === 'researcher'
                              ? '23px'
                              : '3px',
                          bottom: '3px',
                          backgroundColor: 'white',
                          transition: 'left 0.2s',
                          borderRadius: '50%',
                        }}
                      />
                    </span>
                  </label>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setSelectedParticipant(null);
                    setParticipantDetails(null);
                  }}
                  style={{
                    padding: '8px',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={20} color="#6B7280" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {/* Session Details */}
              {/* Session Details with Lock/Unlock Controls */}
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px',
                  }}
                >
                  Session Progress & Access Control
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {participantDetails.user_sessions
                    ?.sort((a, b) => a.session_number - b.session_number)
                    .map((session) => {
                      // Check for manual override
                      const override =
                        participantDetails.manual_session_overrides?.find(
                          (o) => o.session_number === session.session_number,
                        );

                      // Determine if session is unlocked
                      const scheduleUnlockTime =
                        participantDetails.participant_session_schedule?.[
                          `session_${session.session_number}_unlock_time`
                        ];
                      const isScheduledUnlocked =
                        session.session_number === 0
                          ? true
                          : scheduleUnlockTime &&
                            new Date() >= new Date(scheduleUnlockTime);
                      const isManuallyOverridden = !!override;
                      const isUnlocked = isManuallyOverridden
                        ? override.override_status === 'unlocked'
                        : isScheduledUnlocked;

                      return (
                        <div
                          key={session.id}
                          style={{
                            backgroundColor:
                              session.session_fully_completed ||
                              session.session_completed
                                ? '#ECFDF5'
                                : '#F9FAFB',
                            border: '1px solid',
                            borderColor:
                              session.session_fully_completed ||
                              session.session_completed
                                ? '#D1FAE5'
                                : '#E5E7EB',
                            borderRadius: '8px',
                            padding: '16px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '8px',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <h4
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  margin: '0 0 4px 0',
                                }}
                              >
                                {session.session_number === 0
                                  ? 'Introduction Session'
                                  : `Session ${session.session_number}`}
                              </h4>

                              {/* Unlock Status Indicator */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '4px',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: isUnlocked
                                      ? '#ECFDF5'
                                      : '#FEF2F2',
                                    color: isUnlocked ? '#065F46' : '#991B1B',
                                  }}
                                >
                                  {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                                </span>

                                {isManuallyOverridden && (
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: '#FEF3C7',
                                      color: '#92400E',
                                    }}
                                  >
                                    Manual Override
                                  </span>
                                )}
                              </div>

                              {/* Schedule Info */}
                              {scheduleUnlockTime && (
                                <p
                                  style={{
                                    fontSize: '11px',
                                    color: '#6B7280',
                                    margin: '4px 0 0 0',
                                  }}
                                >
                                  Scheduled:{' '}
                                  {new Date(
                                    scheduleUnlockTime,
                                  ).toLocaleString()}
                                </p>
                              )}

                              {/* Override Info */}
                              {isManuallyOverridden && (
                                <p
                                  style={{
                                    fontSize: '11px',
                                    color: '#6B7280',
                                    margin: '4px 0 0 0',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  Override by {override.override_by_email} on{' '}
                                  {new Date(
                                    override.override_at,
                                  ).toLocaleString()}
                                </p>
                              )}
                            </div>

                            {/* Lock/Unlock Controls */}
                            <div
                              style={{
                                display: 'flex',
                                gap: '6px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {isUnlocked ? (
                                <button
                                  onClick={async () => {
                                    if (
                                      !confirm(
                                        `Lock Session ${session.session_number} for ${participantDetails.email}?`,
                                      )
                                    )
                                      return;

                                    try {
                                      const {
                                        data: { user },
                                      } = await supabase.auth.getUser();

                                      const { error } = await supabase
                                        .from('manual_session_overrides')
                                        .upsert(
                                          {
                                            user_id: participantDetails.id,
                                            session_number:
                                              session.session_number,
                                            override_status: 'locked',
                                            override_by: user.id,
                                            override_by_email: user.email,
                                            updated_at:
                                              new Date().toISOString(),
                                          },
                                          {
                                            onConflict:
                                              'user_id,session_number',
                                          },
                                        );

                                      if (error) throw error;

                                      alert('Session locked successfully');
                                      await loadParticipantDetails(
                                        participantDetails.id,
                                      );
                                    } catch (error) {
                                      console.error(
                                        'Error locking session:',
                                        error,
                                      );
                                      alert(
                                        'Error locking session: ' +
                                          error.message,
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#FEF2F2',
                                    color: '#DC2626',
                                    border: '1px solid #FECACA',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  🔒 Lock
                                </button>
                              ) : (
                                <button
                                  onClick={async () => {
                                    if (
                                      !confirm(
                                        `Unlock Session ${session.session_number} for ${participantDetails.email}?`,
                                      )
                                    )
                                      return;

                                    try {
                                      const {
                                        data: { user },
                                      } = await supabase.auth.getUser();

                                      const { error } = await supabase
                                        .from('manual_session_overrides')
                                        .upsert(
                                          {
                                            user_id: participantDetails.id,
                                            session_number:
                                              session.session_number,
                                            override_status: 'unlocked',
                                            override_by: user.id,
                                            override_by_email: user.email,
                                            updated_at:
                                              new Date().toISOString(),
                                          },
                                          {
                                            onConflict:
                                              'user_id,session_number',
                                          },
                                        );

                                      if (error) throw error;

                                      alert('Session unlocked successfully');
                                      await loadParticipantDetails(
                                        participantDetails.id,
                                      );
                                    } catch (error) {
                                      console.error(
                                        'Error unlocking session:',
                                        error,
                                      );
                                      alert(
                                        'Error unlocking session: ' +
                                          error.message,
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#ECFDF5',
                                    color: '#059669',
                                    border: '1px solid #D1FAE5',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  🔓 Unlock
                                </button>
                              )}

                              {/* Remove Override Button */}
                              {isManuallyOverridden && (
                                <button
                                  onClick={async () => {
                                    if (
                                      !confirm(
                                        `Remove manual override for Session ${session.session_number}? It will revert to schedule.`,
                                      )
                                    )
                                      return;

                                    try {
                                      const { error } = await supabase
                                        .from('manual_session_overrides')
                                        .delete()
                                        .eq('user_id', participantDetails.id)
                                        .eq(
                                          'session_number',
                                          session.session_number,
                                        );

                                      if (error) throw error;

                                      alert(
                                        'Override removed - reverted to schedule',
                                      );
                                      await loadParticipantDetails(
                                        participantDetails.id,
                                      );
                                    } catch (error) {
                                      console.error(
                                        'Error removing override:',
                                        error,
                                      );
                                      alert(
                                        'Error removing override: ' +
                                          error.message,
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#F3F4F6',
                                    color: '#6B7280',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ↺ Revert
                                </button>
                              )}

                              {/* NEW: Mark Complete/Incomplete Button */}
                              <button
                                onClick={async () => {
                                  const newStatus =
                                    !session.session_fully_completed;
                                  if (
                                    !confirm(
                                      `Mark Session ${session.session_number} as ${newStatus ? 'complete' : 'incomplete'}?`,
                                    )
                                  )
                                    return;

                                  try {
                                    const { error } = await supabase
                                      .from('user_sessions')
                                      .update({
                                        session_completed: newStatus,
                                        session_fully_completed: newStatus,
                                        updated_at: new Date().toISOString(),
                                      })
                                      .eq('id', session.id);

                                    if (error) throw error;

                                    alert(
                                      `Session ${newStatus ? 'completed' : 'marked incomplete'} successfully`,
                                    );
                                    await loadParticipantDetails(
                                      participantDetails.id,
                                    );
                                    await loadDashboardData();
                                  } catch (error) {
                                    console.error(
                                      'Error updating session completion:',
                                      error,
                                    );
                                    alert(
                                      'Error updating session: ' +
                                        error.message,
                                    );
                                  }
                                }}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor:
                                    session.session_fully_completed
                                      ? '#FEF3C7'
                                      : '#EFF6FF',
                                  color: session.session_fully_completed
                                    ? '#92400E'
                                    : '#1E40AF',
                                  border: '1px solid',
                                  borderColor: session.session_fully_completed
                                    ? '#FDE68A'
                                    : '#BFDBFE',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                {session.session_fully_completed
                                  ? '↩️ Undo Complete'
                                  : '✓ Mark Complete'}
                              </button>
                            </div>
                          </div>
                          {/* Session progress details with admin controls */}
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '8px',
                              fontSize: '12px',
                            }}
                          >
                            {/* Video */}
                            <div
                              onClick={async () => {
                                if (
                                  !confirm(
                                    `Mark video as ${session.video_completed ? 'incomplete' : 'complete'}?`,
                                  )
                                )
                                  return;

                                try {
                                  const { error } = await supabase
                                    .from('user_sessions')
                                    .update({
                                      video_completed: !session.video_completed,
                                      video_watched: !session.video_completed,
                                      updated_at: new Date().toISOString(),
                                    })
                                    .eq('id', session.id);

                                  if (error) throw error;

                                  await loadParticipantDetails(
                                    participantDetails.id,
                                  );
                                } catch (error) {
                                  console.error(
                                    'Error updating video status:',
                                    error,
                                  );
                                  alert(
                                    'Error updating video status: ' +
                                      error.message,
                                  );
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  '#F9FAFB')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  'transparent')
                              }
                            >
                              <CheckCircle
                                size={14}
                                color={
                                  session.video_completed
                                    ? '#10B981'
                                    : '#D1D5DB'
                                }
                              />
                              <span
                                style={{
                                  color: session.video_completed
                                    ? '#065F46'
                                    : '#6B7280',
                                }}
                              >
                                Video{' '}
                                {session.video_completed
                                  ? 'Completed'
                                  : 'Pending'}
                              </span>
                            </div>

                            {/* Quiz */}
                            {session.session_number !== 0 && (
                              <div
                                onClick={async () => {
                                  if (
                                    !confirm(
                                      `Mark quiz as ${session.quiz_completed ? 'incomplete' : 'complete'}?`,
                                    )
                                  )
                                    return;

                                  try {
                                    const { error } = await supabase
                                      .from('user_sessions')
                                      .update({
                                        quiz_completed: !session.quiz_completed,
                                        updated_at: new Date().toISOString(),
                                      })
                                      .eq('id', session.id);

                                    if (error) throw error;

                                    await loadParticipantDetails(
                                      participantDetails.id,
                                    );
                                  } catch (error) {
                                    console.error(
                                      'Error updating quiz status:',
                                      error,
                                    );
                                    alert(
                                      'Error updating quiz status: ' +
                                        error.message,
                                    );
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    '#F9FAFB')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    'transparent')
                                }
                              >
                                <CheckCircle
                                  size={14}
                                  color={
                                    session.quiz_completed
                                      ? '#10B981'
                                      : '#D1D5DB'
                                  }
                                />
                                <span
                                  style={{
                                    color: session.quiz_completed
                                      ? '#065F46'
                                      : '#6B7280',
                                  }}
                                >
                                  Quiz{' '}
                                  {session.quiz_completed
                                    ? 'Completed'
                                    : 'Pending'}
                                </span>
                              </div>
                            )}

                            {/* Pre-Assessment */}
                            {session.session_number !== 0 &&
                              session.session_number !== 1 && (
                                <div
                                  onClick={async () => {
                                    if (
                                      !confirm(
                                        `Mark pre-assessment as ${session.pre_assessment_completed ? 'incomplete' : 'complete'}?`,
                                      )
                                    )
                                      return;

                                    try {
                                      const { error } = await supabase
                                        .from('user_sessions')
                                        .update({
                                          pre_assessment_completed:
                                            !session.pre_assessment_completed,
                                          updated_at: new Date().toISOString(),
                                        })
                                        .eq('id', session.id);

                                      if (error) throw error;

                                      await loadParticipantDetails(
                                        participantDetails.id,
                                      );
                                    } catch (error) {
                                      console.error(
                                        'Error updating pre-assessment status:',
                                        error,
                                      );
                                      alert(
                                        'Error updating pre-assessment status: ' +
                                          error.message,
                                      );
                                    }
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      '#F9FAFB')
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      'transparent')
                                  }
                                >
                                  <CheckCircle
                                    size={14}
                                    color={
                                      session.pre_assessment_completed
                                        ? '#10B981'
                                        : '#D1D5DB'
                                    }
                                  />
                                  <span
                                    style={{
                                      color: session.pre_assessment_completed
                                        ? '#065F46'
                                        : '#6B7280',
                                    }}
                                  >
                                    Pre-Assessment{' '}
                                    {session.pre_assessment_completed
                                      ? 'Done'
                                      : 'Pending'}
                                  </span>
                                </div>
                              )}

                            {/* Post-Assessment */}
                            {/* {session.session_number !== 0 && (
    <div
      onClick={async () => {
        if (!confirm(`Mark post-assessment as ${session.post_assessment_completed ? 'incomplete' : 'complete'}?`)) return;
        
        try {
          const { error } = await supabase
            .from('user_sessions')
            .update({
              post_assessment_completed: !session.post_assessment_completed,
              updated_at: new Date().toISOString()
            })
            .eq('id', session.id);

          if (error) throw error;
          
          await loadParticipantDetails(participantDetails.id);
        } catch (error) {
          console.error('Error updating post-assessment status:', error);
          alert('Error updating post-assessment status: ' + error.message);
        }
      }}
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '4px',
        transition: 'background-color 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <CheckCircle size={14} color={session.post_assessment_completed ? '#10B981' : '#D1D5DB'} />
      <span style={{ color: session.post_assessment_completed ? '#065F46' : '#6B7280' }}>
        Post-Assessment {session.post_assessment_completed ? 'Done' : 'Pending'}
      </span>
    </div>
  )}*/}
                          </div>
                          {session.updated_at && (
                            <p
                              style={{
                                fontSize: '11px',
                                color: '#9CA3AF',
                                margin: '8px 0 0 0',
                              }}
                            >
                              Last updated:{' '}
                              {new Date(session.updated_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
              {/* Practice Logs */}
              {console.log(
                '🔍 Practice logs:',
                JSON.stringify(participantDetails.practice_logs?.[0], null, 2),
              )}
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px',
                  }}
                >
                  Practice Logs ({participantDetails.practice_logs?.length || 0}
                  )
                </h3>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '12px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1D4ED8',
                      border: '1px solid #BFDBFE',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    Current week: {weeklyPracticeCounts.currentWeekCount}
                  </div>
                  <div
                    style={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #E5E7EB',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    Last week: {weeklyPracticeCounts.lastWeekCount}
                  </div>
                </div>
                {participantDetails.practice_logs &&
                participantDetails.practice_logs.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gap: '8px',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {participantDetails.practice_logs.map((log) => (
                      <div
                        key={log.id}
                        style={{
                          backgroundColor: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '12px',
                          fontSize: '13px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '4px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span style={{ fontWeight: '500' }}>
                              {new Date(
                                log.practice_date || log.created_at,
                              ).toLocaleDateString()}
                            </span>
                            {log.skill && (
                              <span
                                style={{
                                  backgroundColor: '#DBEAFE',
                                  color: '#1E40AF',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                }}
                              >
                                {log.skill}
                              </span>
                            )}
                            {log.practice_type && (
                              <span
                                style={{
                                  backgroundColor: '#F3E8FF',
                                  color: '#7C3AED',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                }}
                              >
                                {log.practice_type}
                              </span>
                            )}
                          </div>
                          <span style={{ color: '#6B7280' }}>
                            {log.minutes} min
                          </span>
                        </div>
                        {log.session_number && (
                          <p
                            style={{
                              fontSize: '11px',
                              color: '#9CA3AF',
                              margin: 0,
                            }}
                          >
                            Session {log.session_number}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      fontStyle: 'italic',
                    }}
                  >
                    No practice logs recorded
                  </p>
                )}
              </div>

              {/* Schedule Information */}
              {participantDetails.participant_session_schedule && (
                <div>
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '12px',
                    }}
                  >
                    Session Schedule
                  </h3>
                  <div
                    style={{
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <p style={{ fontSize: '13px', margin: '0 0 12px 0' }}>
                      <strong>Start Date:</strong>{' '}
                      {new Date(
                        participantDetails.participant_session_schedule
                          .session_1_start_date,
                      ).toLocaleDateString()}
                    </p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gap: '8px',
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((num) => {
                        const unlockTime =
                          participantDetails.participant_session_schedule[
                            `session_${num}_unlock_time`
                          ];
                        const isUnlocked = new Date() >= new Date(unlockTime);
                        const unlockDate = new Date(unlockTime);
                        return (
                          <div
                            key={num}
                            style={{
                              textAlign: 'center',
                              padding: '8px',
                              backgroundColor: isUnlocked
                                ? '#ECFDF5'
                                : '#FEF2F2',
                              border: '1px solid',
                              borderColor: isUnlocked ? '#D1FAE5' : '#FECACA',
                              borderRadius: '6px',
                            }}
                          >
                            <p
                              style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                margin: '0 0 4px 0',
                              }}
                            >
                              Session {num}
                            </p>
                            <p
                              style={{
                                fontSize: '10px',
                                color: isUnlocked ? '#065F46' : '#991B1B',
                                margin: '0 0 4px 0',
                                fontWeight: '600',
                              }}
                            >
                              {isUnlocked ? 'Unlocked' : 'Locked'}
                            </p>
                            <p
                              style={{
                                fontSize: '9px',
                                color: '#6B7280',
                                margin: 0,
                                lineHeight: '1.2',
                              }}
                            >
                              {unlockDate.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                              <br />
                              {unlockDate.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    {participantDetails.participant_session_schedule.notes && (
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: '12px 0 0 0',
                        }}
                      >
                        <strong>Notes:</strong>{' '}
                        {participantDetails.participant_session_schedule.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #F3F4F6',
          padding: '32px 0',
        }}
      >
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1
            style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              margin: 0,
            }}
          >
            Researcher Dashboard
          </h1>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleViewParticipantDashboard}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              <Eye size={16} />
              View Dashboard
            </button>

            <button
              onClick={() => setShowHelpPanel(true)}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
              title="Help"
            >
              <HelpCircle size={16} />
            </button>

            <button
              onClick={async () => {
                const { error } = await supabase.auth.signOut();
                if (error) {
                  console.error('Error signing out:', error);
                } else {
                  window.location.href = '/login';
                }
              }}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FEF2F2';
                e.currentTarget.style.borderColor = '#FCA5A5';
                e.currentTarget.style.color = '#DC2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.color = '#6B7280';
              }}
              title="Log Out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          maxWidth: '1400px',
          margin: '24px auto',
          padding: '0 24px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Users style={{ color: '#6B7280' }} size={20} />
              <div>
                <p
                  style={{
                    fontSize: '32px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {stats.totalParticipants}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Total Participants
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Activity style={{ color: '#10B981' }} size={20} />
              <div>
                <p
                  style={{
                    fontSize: '32px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {stats.activeParticipants}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Active (Last 7 Days)
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CheckCircle style={{ color: '#3B82F6' }} size={20} />
              <div>
                <p
                  style={{
                    fontSize: '32px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {stats.completedProgram}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Completed Program
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText style={{ color: '#F59E0B' }} size={20} />
              <div>
                <p
                  style={{
                    fontSize: '32px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {stats.totalPracticeLogs}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Practice Logs
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <BarChart3 style={{ color: '#EC4899' }} size={20} />
              <div>
                <p
                  style={{
                    fontSize: '32px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {stats.assessmentCompletionRate.toFixed(0)}%
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Assessment Rate
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            {[
              'Participants',
              'Activity Monitor',
              'Assessment Links',
              'Session Scheduling',
              'User Management',
              'Data Export',
            ].map((tab) => (
              <button
                key={tab}
                onClick={() =>
                  setActiveTab(tab.toLowerCase().replace(' ', '-'))
                }
                style={{
                  padding: '16px 24px',
                  backgroundColor:
                    activeTab === tab.toLowerCase().replace(' ', '-')
                      ? '#EFF6FF'
                      : 'white',
                  color:
                    activeTab === tab.toLowerCase().replace(' ', '-')
                      ? '#1B365D'
                      : '#6B7280',
                  border: 'none',
                  borderBottom:
                    activeTab === tab.toLowerCase().replace(' ', '-')
                      ? '2px solid #1B365D'
                      : 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ padding: '24px' }}>
            {/* Participants Tab */}
            {activeTab === 'participants' && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '20px',
                    borderBottom: '1px solid #E5E7EB',
                    paddingBottom: '8px',
                  }}
                >
                  <button
                    onClick={() => setParticipantCategory('active')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor:
                        participantCategory === 'active' ? '#1B365D' : 'white',
                      color:
                        participantCategory === 'active' ? 'white' : '#6B7280',
                      border: '1px solid',
                      borderColor:
                        participantCategory === 'active'
                          ? '#1B365D'
                          : '#E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <UserCheck size={16} />
                    Active Participants
                    <span
                      style={{
                        backgroundColor:
                          participantCategory === 'active'
                            ? 'rgba(255,255,255,0.2)'
                            : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {categoryCounts.active}
                    </span>
                  </button>

                  <button
                    onClick={() => setParticipantCategory('completed')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor:
                        participantCategory === 'completed'
                          ? '#10B981'
                          : 'white',
                      color:
                        participantCategory === 'completed'
                          ? 'white'
                          : '#6B7280',
                      border: '1px solid',
                      borderColor:
                        participantCategory === 'completed'
                          ? '#10B981'
                          : '#E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <CheckCircle size={16} />
                    Completed
                    <span
                      style={{
                        backgroundColor:
                          participantCategory === 'completed'
                            ? 'rgba(255,255,255,0.2)'
                            : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {categoryCounts.completed}
                    </span>
                  </button>

                  <button
                    onClick={() => setParticipantCategory('pending')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor:
                        participantCategory === 'pending' ? '#F59E0B' : 'white',
                      color:
                        participantCategory === 'pending' ? 'white' : '#6B7280',
                      border: '1px solid',
                      borderColor:
                        participantCategory === 'pending'
                          ? '#F59E0B'
                          : '#E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Clock size={16} />
                    Pending Users
                    <span
                      style={{
                        backgroundColor:
                          participantCategory === 'pending'
                            ? 'rgba(255,255,255,0.2)'
                            : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {categoryCounts.pending}
                    </span>
                  </button>

                  <button
                    onClick={() => setParticipantCategory('test')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor:
                        participantCategory === 'test' ? '#6B7280' : 'white',
                      color:
                        participantCategory === 'test' ? 'white' : '#6B7280',
                      border: '1px solid',
                      borderColor:
                        participantCategory === 'test' ? '#6B7280' : '#E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    Test Accounts
                    <span
                      style={{
                        backgroundColor:
                          participantCategory === 'test'
                            ? 'rgba(255,255,255,0.2)'
                            : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {categoryCounts.test}
                    </span>
                  </button>
                  <button
                    onClick={() => setParticipantCategory('admin')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor:
                        participantCategory === 'admin' ? '#7C3AED' : 'white',
                      color:
                        participantCategory === 'admin' ? 'white' : '#6B7280',
                      border: '1px solid',
                      borderColor:
                        participantCategory === 'admin' ? '#7C3AED' : '#E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <UserCheck size={16} />
                    Admin Users
                    <span
                      style={{
                        backgroundColor:
                          participantCategory === 'admin'
                            ? 'rgba(255,255,255,0.2)'
                            : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {categoryCounts.admin}
                    </span>
                  </button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ flex: 1, position: 'relative' }}>
                    <Search
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF',
                      }}
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search by email or participant ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 40px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                      padding: '10px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                  </select>
                  <button
                    onClick={exportData}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#1B365D',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    <Download size={16} />
                    Export CSV
                  </button>
                </div>

                {participantCategory === 'pending' && (
                  <div
                    style={{
                      backgroundColor: '#FEF3C7',
                      border: '1px solid #FDE68A',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      color: '#92400E',
                    }}
                  >
                    These users have not been assigned a patient ID yet and
                    cannot access the full program.
                  </div>
                )}

                {/* Participants Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Participant
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Sessions
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Progress
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Last Activity
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Practice
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Assessments
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParticipants.map((participant) => (
                        <tr
                          key={participant.id}
                          style={{
                            borderBottom: '1px solid #F3F4F6',
                            transition: 'background-color 0.2s',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = '#F9FAFB')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              'transparent')
                          }
                        >
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <p
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#111827',
                                  margin: '0 0 4px 0',
                                }}
                              >
                                {participant.email}
                              </p>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: '12px',
                                    color: '#6B7280',
                                    margin: 0,
                                  }}
                                >
                                  ID:{' '}
                                  {participant.patient_id ||
                                    participant.id.substring(0, 8)}
                                </p>
                                {participant.rawSessions?.find(
                                  (s) =>
                                    s.session_number === 0 && s.video_completed,
                                ) && (
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: '#DBEAFE',
                                      color: '#1E40AF',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                    }}
                                  >
                                    <CheckCircle size={10} />
                                    Intro
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: `${getStatusColor(participant.daysSinceLastActivity)}20`,
                                color: getStatusColor(
                                  participant.daysSinceLastActivity,
                                ),
                              }}
                            >
                              <div
                                style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: getStatusColor(
                                    participant.daysSinceLastActivity,
                                  ),
                                }}
                              />
                              <span
                                style={{ fontSize: '12px', fontWeight: '500' }}
                              >
                                {getStatusLabel(
                                  participant.daysSinceLastActivity,
                                )}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <p
                                style={{
                                  fontSize: '13px',
                                  color: '#111827',
                                  margin: '0 0 2px 0',
                                }}
                              >
                                {participant.sessionsStarted || 0} started
                              </p>
                              <p
                                style={{
                                  fontSize: '11px',
                                  color: '#6B7280',
                                  margin: 0,
                                }}
                              >
                                {participant.videosWatched || 0} videos,{' '}
                                {participant.quizzesCompleted || 0} quizzes
                              </p>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: '8px',
                                    backgroundColor: '#E5E7EB',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${participant.progress}%`,
                                      height: '100%',
                                      backgroundColor:
                                        participant.progress === 100
                                          ? '#10B981'
                                          : '#3B82F6',
                                      transition: 'width 0.3s',
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontSize: '12px',
                                    color: '#6B7280',
                                    minWidth: '40px',
                                  }}
                                >
                                  {participant.progress.toFixed(0)}%
                                </span>
                              </div>
                              <p
                                style={{
                                  fontSize: '11px',
                                  color: '#9CA3AF',
                                  margin: '4px 0 0 0',
                                }}
                              >
                                {participant.completedSessions}/
                                {participant.totalSessions} sessions
                              </p>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <p
                                style={{
                                  fontSize: '13px',
                                  color: '#111827',
                                  margin: '0 0 2px 0',
                                }}
                              >
                                {participant.lastActivity.toLocaleDateString()}
                              </p>
                              <p
                                style={{
                                  fontSize: '11px',
                                  color: '#6B7280',
                                  margin: 0,
                                }}
                              >
                                {participant.daysSinceLastActivity} days ago
                              </p>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <p
                                style={{
                                  fontSize: '13px',
                                  color: '#111827',
                                  margin: '0 0 2px 0',
                                }}
                              >
                                {participant.practiceLogCount} logs
                              </p>
                              <p
                                style={{
                                  fontSize: '11px',
                                  color: '#6B7280',
                                  margin: 0,
                                }}
                              >
                                {participant.totalPracticeMinutes} min total
                              </p>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '2px',
                                  marginBottom: '8px',
                                }}
                              >
                                {[1, 2, 3, 4, 5].map((sessionNum) => {
                                  const assessment =
                                    participant.assessmentsBySession[
                                      sessionNum
                                    ];
                                  if (!assessment) return null;

                                  const hasPre =
                                    sessionNum !== 1 && assessment.pre;
                                  // const hasPost = assessment.post;

                                  return (
                                    <div
                                      key={sessionNum}
                                      title={`Session ${sessionNum}: Pre ${hasPre ? '✓' : '✗'}`}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        cursor: 'help',
                                      }}
                                    >
                                      {sessionNum !== 1 ? (
                                        // Sessions 2-5: Pre on top, Post on bottom
                                        <>
                                          <div
                                            style={{
                                              width: '16px',
                                              height: '8px',
                                              backgroundColor: hasPre
                                                ? '#10B981'
                                                : '#E5E7EB',
                                              borderRadius: '2px',
                                            }}
                                          />
                                          {/* <div style={{
                  width: '16px',
                  height: '8px',
                  backgroundColor: hasPost ? '#3B82F6' : '#E5E7EB',
                  borderRadius: '2px'
                }} /> */}
                                        </>
                                      ) : (
                                        // Session 1: Empty space on top, Post on bottom
                                        <>
                                          <div
                                            style={{
                                              width: '16px',
                                              height: '8px',
                                              backgroundColor: 'transparent',
                                              borderRadius: '2px',
                                            }}
                                          />
                                          {/* <div style={{
                  width: '16px',
                  height: '8px',
                  backgroundColor: hasPost ? '#3B82F6' : '#E5E7EB',
                  borderRadius: '2px'
                }} /> */}
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <p
                                style={{
                                  fontSize: '11px',
                                  color: '#6B7280',
                                  margin: 0,
                                  display: 'flex',
                                  gap: '8px',
                                }}
                              >
                                <span>
                                  Pre: {participant.completedPreAssessments}/
                                  {participant.totalExpectedPreAssessments}
                                </span>
                              </p>
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              <button
                                onClick={() =>
                                  loadParticipantDetails(participant.id)
                                }
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '6px 12px',
                                  backgroundColor: '#F3F4F6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                }}
                              >
                                <Eye size={14} />
                                View
                              </button>

                              <div style={{ position: 'relative' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(
                                      openDropdown === participant.id
                                        ? null
                                        : participant.id,
                                    );
                                  }}
                                  style={{
                                    padding: '6px',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <MoreVertical size={16} color="#6B7280" />
                                </button>

                                {openDropdown === participant.id && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      top: '100%',
                                      marginTop: '4px',
                                      backgroundColor: 'white',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '8px',
                                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                      zIndex: 100,
                                      minWidth: '180px',
                                    }}
                                  >
                                    {getParticipantCategory(participant) !==
                                      'active' && (
                                      <button
                                        onClick={() =>
                                          moveParticipantToCategory(
                                            participant.id,
                                            'active',
                                          )
                                        }
                                        style={{
                                          width: '100%',
                                          padding: '10px 16px',
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          borderBottom: '1px solid #F3F4F6',
                                          fontSize: '13px',
                                          color: '#374151',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            '#F9FAFB')
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            'transparent')
                                        }
                                      >
                                        <UserCheck size={14} />
                                        Move to Active
                                      </button>
                                    )}
                                    {getParticipantCategory(participant) !==
                                      'pending' && (
                                      <button
                                        onClick={() =>
                                          moveParticipantToCategory(
                                            participant.id,
                                            'pending',
                                          )
                                        }
                                        style={{
                                          width: '100%',
                                          padding: '10px 16px',
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          borderBottom: '1px solid #F3F4F6',
                                          fontSize: '13px',
                                          color: '#374151',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            '#F9FAFB')
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            'transparent')
                                        }
                                      >
                                        <Clock size={14} />
                                        Move to Pending
                                      </button>
                                    )}
                                    {getParticipantCategory(participant) !==
                                      'test' && (
                                      <button
                                        onClick={() =>
                                          moveParticipantToCategory(
                                            participant.id,
                                            'test',
                                          )
                                        }
                                        style={{
                                          width: '100%',
                                          padding: '10px 16px',
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          borderBottom: '1px solid #F3F4F6',
                                          fontSize: '13px',
                                          color: '#374151',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            '#F9FAFB')
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            'transparent')
                                        }
                                      >
                                        Move to Test
                                      </button>
                                    )}
                                    {getParticipantCategory(participant) !==
                                      'completed' && (
                                      <button
                                        onClick={() =>
                                          moveParticipantToCategory(
                                            participant.id,
                                            'completed',
                                          )
                                        }
                                        style={{
                                          width: '100%',
                                          padding: '10px 16px',
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          fontSize: '13px',
                                          color: '#374151',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            '#F9FAFB')
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            'transparent')
                                        }
                                      >
                                        <CheckCircle size={14} />
                                        Move to Completed
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredParticipants.length === 0 && (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '48px',
                        color: '#6B7280',
                      }}
                    >
                      <Users
                        size={48}
                        style={{ margin: '0 auto 16px', opacity: 0.3 }}
                      />
                      <p
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          margin: '0 0 8px 0',
                        }}
                      >
                        No participants found
                      </p>
                      <p style={{ fontSize: '14px', margin: 0 }}>
                        {participantCategory === 'pending' &&
                          'No pending users to display'}
                        {participantCategory === 'test' &&
                          'No test accounts have been marked'}
                        {participantCategory === 'active' &&
                          'No active participants match your filters'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Activity Monitor Tab */}
            {activeTab === 'activity-monitor' && (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {/* Active Today */}
                  <div
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#166534',
                        margin: '0 0 12px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#10B981',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite',
                        }}
                      />
                      Active Today
                    </h3>
                    {participants
                      .filter(
                        (p) =>
                          p.daysSinceLastActivity === 0 &&
                          !participantCategories.test.includes(p.id),
                      )
                      .slice(0, 5)
                      .map((p) => (
                        <div
                          key={p.id}
                          style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #D1FAE5',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#166534',
                              margin: '0 0 2px 0',
                            }}
                          >
                            {p.email}
                          </p>
                          <p
                            style={{
                              fontSize: '11px',
                              color: '#16A34A',
                              margin: 0,
                            }}
                          >
                            Session {p.completedSessions} •{' '}
                            {new Date(p.lastActivity).toLocaleTimeString()}
                          </p>
                        </div>
                      ))}
                    {participants.filter(
                      (p) =>
                        p.daysSinceLastActivity === 0 &&
                        !participantCategories.test.includes(p.id),
                    ).length === 0 && (
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#16A34A',
                          fontStyle: 'italic',
                        }}
                      >
                        No participants active today
                      </p>
                    )}
                  </div>

                  {/* Need Attention */}
                  <div
                    style={{
                      backgroundColor: '#FEF3C7',
                      border: '1px solid #FDE68A',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#92400E',
                        margin: '0 0 12px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <AlertCircle size={16} color="#F59E0B" />
                      Need Attention (3-7 days)
                    </h3>
                    {participants
                      .filter(
                        (p) =>
                          p.daysSinceLastActivity >= 3 &&
                          p.daysSinceLastActivity <= 7 &&
                          !participantCategories.test.includes(p.id),
                      )
                      .slice(0, 5)
                      .map((p) => (
                        <div
                          key={p.id}
                          style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #FED7AA',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#92400E',
                              margin: '0 0 2px 0',
                            }}
                          >
                            {p.email}
                          </p>
                          <p
                            style={{
                              fontSize: '11px',
                              color: '#D97706',
                              margin: 0,
                            }}
                          >
                            Last active {p.daysSinceLastActivity} days ago •
                            Progress: {p.progress.toFixed(0)}%
                          </p>
                        </div>
                      ))}
                  </div>
                  {/* Inactive */}
                  <div
                    style={{
                      backgroundColor: '#FEE2E2',
                      border: '1px solid #FECACA',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#991B1B',
                        margin: '0 0 12px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <X size={16} color="#EF4444" />
                      Inactive (7+ days)
                    </h3>
                    {participants
                      .filter(
                        (p) =>
                          p.daysSinceLastActivity > 7 &&
                          !participantCategories.test.includes(p.id),
                      )
                      .slice(0, 5)
                      .map((p) => (
                        <div
                          key={p.id}
                          style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #FECACA',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#991B1B',
                              margin: '0 0 2px 0',
                            }}
                          >
                            {p.email}
                          </p>
                          <p
                            style={{
                              fontSize: '11px',
                              color: '#DC2626',
                              margin: 0,
                            }}
                          >
                            Last active {p.daysSinceLastActivity} days ago
                          </p>
                        </div>
                      ))}
                  </div>

                  {/* Completed Program */}
                  <div
                    style={{
                      backgroundColor: '#ECFDF5',
                      border: '1px solid #A7F3D0',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#065F46',
                        margin: '0 0 12px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckCircle size={16} color="#10B981" />
                      Completed Program (
                      {
                        participants.filter(
                          (p) =>
                            p.progress === 100 &&
                            !participantCategories.test.includes(p.id),
                        ).length
                      }
                      )
                    </h3>
                    {participants
                      .filter(
                        (p) =>
                          p.progress === 100 &&
                          !participantCategories.test.includes(p.id),
                      )
                      .slice(0, 5)
                      .map((p) => (
                        <div
                          key={p.id}
                          style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #D1FAE5',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#065F46',
                              margin: '0 0 2px 0',
                            }}
                          >
                            {p.email}
                          </p>
                          <p
                            style={{
                              fontSize: '11px',
                              color: '#059669',
                              margin: 0,
                            }}
                          >
                            All 5 sessions completed • {p.practiceLogCount}{' '}
                            practice logs
                          </p>
                        </div>
                      ))}
                    {participants.filter(
                      (p) =>
                        p.progress === 100 &&
                        !participantCategories.test.includes(p.id),
                    ).length === 0 && (
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#059669',
                          fontStyle: 'italic',
                        }}
                      >
                        No participants have completed the program yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Practice Activity Chart */}
                <div
                  style={{
                    marginTop: '24px',
                    padding: '20px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827',
                      margin: '0 0 16px 0',
                    }}
                  >
                    Practice Logging Activity (Active Participants Only)
                  </h3>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '12px',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <p
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#10B981',
                          margin: '0 0 4px 0',
                        }}
                      >
                        {
                          participants.filter(
                            (p) =>
                              p.practiceLogCount > 0 &&
                              !participantCategories.test.includes(p.id),
                          ).length
                        }
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0,
                        }}
                      >
                        Logging Practice
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#F59E0B',
                          margin: '0 0 4px 0',
                        }}
                      >
                        {
                          participants.filter(
                            (p) =>
                              p.practiceLogCount === 0 &&
                              p.completedSessions > 0 &&
                              !participantCategories.test.includes(p.id),
                          ).length
                        }
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0,
                        }}
                      >
                        Not Logging
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p
                        style={{
                          fontSize: '24px',
                          fontWeight: '600',
                          color: '#3B82F6',
                          margin: '0 0 4px 0',
                        }}
                      >
                        {Math.round(
                          participants
                            .filter(
                              (p) => !participantCategories.test.includes(p.id),
                            )
                            .reduce(
                              (sum, p) => sum + p.totalPracticeMinutes,
                              0,
                            ) /
                            participants.filter(
                              (p) =>
                                p.practiceLogCount > 0 &&
                                !participantCategories.test.includes(p.id),
                            ).length,
                        ) || 0}
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0,
                        }}
                      >
                        Avg Minutes/User
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Assessment Links Tab */}
            {activeTab === 'assessment-links' && (
              <div>
                <div
                  style={{
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0',
                    }}
                  >
                    Select Session to Configure
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((sessionNum) => (
                      <button
                        key={sessionNum}
                        onClick={() => {
                          setSelectedSession(sessionNum);
                          setSelectedAssessmentParticipant(null);
                          setEditingLinks({ preSessionUrl: '' });
                          setSearchAssessmentEmail('');
                          setLinksSaveMessage('');
                          setLinksErrorMessage('');
                        }}
                        style={{
                          backgroundColor:
                            selectedSession === sessionNum
                              ? '#1B365D'
                              : 'white',
                          color:
                            selectedSession === sessionNum
                              ? 'white'
                              : '#374151',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                        }}
                      >
                        Session {sessionNum}
                      </button>
                    ))}
                  </div>

                  <div
                    style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #DBEAFE',
                      backgroundColor: '#EFF6FF',
                      color: '#1E3A8A',
                      fontSize: '13px',
                      fontWeight: '500',
                    }}
                  >
                    Editing Session {selectedSession}
                    {selectedAssessmentParticipant?.email
                      ? ` for ${selectedAssessmentParticipant.email}`
                      : ' (no participant selected)'}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '24px',
                  }}
                >
                  {/* Add/Edit Links */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '24px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 20px 0',
                      }}
                    >
                      {selectedAssessmentParticipant
                        ? 'Edit Assessment Links'
                        : 'Add Assessment Links'}
                    </h3>

                    {!selectedAssessmentParticipant && (
                      <div style={{ marginBottom: '20px' }}>
                        <input
                          type="email"
                          value={searchAssessmentEmail}
                          onChange={(e) =>
                            setSearchAssessmentEmail(e.target.value)
                          }
                          placeholder="Search participant by email..."
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '14px',
                            marginBottom: '12px',
                          }}
                        />

                        {searchAssessmentEmail && (
                          <div
                            style={{
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              maxHeight: '200px',
                              overflow: 'auto',
                            }}
                          >
                            {searchAssessmentParticipants().map(
                              (participant) => (
                                <div
                                  key={participant.id}
                                  onClick={() =>
                                    selectAssessmentParticipant(participant)
                                  }
                                  style={{
                                    padding: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #F3F4F6',
                                  }}
                                >
                                  {participant.email}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedAssessmentParticipant && (
                      <div>
                        <div
                          style={{
                            backgroundColor: '#F9FAFB',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                          }}
                        >
                          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>
                            {selectedAssessmentParticipant.email}
                          </p>
                          <button
                            onClick={() => {
                              setSelectedAssessmentParticipant(null);
                              setEditingLinks({
                                preSessionUrl: '',
                              });
                              setLinksSaveMessage('');
                              setLinksErrorMessage('');
                            }}
                            style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            Cancel
                          </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label
                            style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              display: 'block',
                              marginBottom: '8px',
                            }}
                          >
                            Pre-Session Assessment URL
                          </label>
                          <input
                            type="url"
                            value={editingLinks.preSessionUrl}
                            onChange={(e) =>
                              {
                                setEditingLinks((prev) => ({
                                  ...prev,
                                  preSessionUrl: e.target.value,
                                }));
                                setLinksSaveMessage('');
                                setLinksErrorMessage('');
                              }
                            }
                            placeholder="https://redcap.example.com/..."
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '13px',
                            }}
                          />
                        </div>

                        <button
                          onClick={saveParticipantLinks}
                          disabled={
                            savingLinks || !editingLinks.preSessionUrl
                          }
                          style={{
                            backgroundColor: '#1B365D',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: savingLinks ? 'not-allowed' : 'pointer',
                            opacity: savingLinks ? 0.5 : 1,
                            width: '100%',
                          }}
                        >
                          {savingLinks ? 'Saving...' : 'Save Links'}
                        </button>

                        {linksSaveMessage && (
                          <div
                            style={{
                              marginTop: '12px',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: '1px solid #86EFAC',
                              backgroundColor: '#F0FDF4',
                              color: '#166534',
                              fontSize: '13px',
                              fontWeight: '500',
                            }}
                          >
                            {linksSaveMessage}
                          </div>
                        )}

                        {linksErrorMessage && (
                          <div
                            style={{
                              marginTop: '12px',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: '1px solid #FECACA',
                              backgroundColor: '#FEF2F2',
                              color: '#991B1B',
                              fontSize: '13px',
                              fontWeight: '500',
                            }}
                          >
                            {linksErrorMessage}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Configured Links */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '20px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          margin: '0',
                        }}
                      >
                        Session {selectedSession} Links (
                        {participantLinks.length})
                      </h3>
                    </div>

                    <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                      {loadingLinks ? (
                        <div
                          style={{
                            padding: '48px 24px',
                            textAlign: 'center',
                            color: '#6B7280',
                          }}
                        >
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              border: '3px solid #E5E7EB',
                              borderTop: '3px solid #1B365D',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              margin: '0 auto 12px',
                            }}
                          ></div>
                          <p style={{ margin: 0 }}>
                            Loading Session {selectedSession} links...
                          </p>
                        </div>
                      ) : participantLinks.length > 0 ? (
                        participantLinks.map((participant) => (
                          <div
                            key={participant.id}
                            style={{
                              padding: '16px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            <p
                              style={{ margin: '0 0 8px 0', fontWeight: '500' }}
                            >
                              {participant.email}
                            </p>
                            <div
                              style={{
                                display: 'flex',
                                gap: '6px',
                                marginBottom: '8px',
                              }}
                            >
                              <span
                                style={{
                                  backgroundColor: participant.preSessionUrl
                                    ? '#ECFDF5'
                                    : '#F3F4F6',
                                  color: participant.preSessionUrl
                                    ? '#065F46'
                                    : '#6B7280',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                }}
                              >
                                Pre:{' '}
                                {participant.preSessionUrl ? 'Set' : 'Missing'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => {
                                  const participantData = {
                                    id: participant.id,
                                    email: participant.email,
                                    created_at: participant.createdAt,
                                  };
                                  selectAssessmentParticipant(participantData);
                                }}
                                style={{
                                  backgroundColor: '#F3F4F6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  removeParticipantLinks(
                                    participant.id,
                                    participant.email,
                                  )
                                }
                                style={{
                                  backgroundColor: '#FEF2F2',
                                  color: '#DC2626',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '48px 24px',
                            textAlign: 'center',
                            color: '#6B7280',
                          }}
                        >
                          <p>
                            No assessment links for Session {selectedSession}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Session Scheduling Tab */}
            {activeTab === 'session-scheduling' && (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '24px',
                  }}
                >
                  {/* Set Schedule */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '24px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: '0 0 20px 0',
                      }}
                    >
                      Set Session Schedule
                    </h3>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Select Participant
                      </label>
                      <select
                        value={schedulingParticipant}
                        onChange={(e) =>
                          setSchedulingParticipant(e.target.value)
                        }
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      >
                        <option value="">Choose participant...</option>
                        {allParticipants.map((participant) => (
                          <option key={participant.id} value={participant.id}>
                            {participant.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Session 1 Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        min={minScheduleDate}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          marginBottom: '12px',
                        }}
                      />
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Notes (Optional)
                      </label>
                      <textarea
                        value={scheduleNotes}
                        onChange={(e) => setScheduleNotes(e.target.value)}
                        placeholder="Add notes..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '13px',
                          resize: 'vertical',
                        }}
                      />
                    </div>

                    <button
                      onClick={saveSessionSchedule}
                      disabled={
                        savingSchedule || !schedulingParticipant || !startDate
                      }
                      style={{
                        backgroundColor: '#7C3AED',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: savingSchedule ? 'not-allowed' : 'pointer',
                        opacity: savingSchedule ? 0.5 : 1,
                        width: '100%',
                      }}
                    >
                      {savingSchedule ? 'Saving...' : 'Save Schedule'}
                    </button>
                  </div>

                  {/* Scheduled Participants */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '20px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          margin: '0',
                        }}
                      >
                        Scheduled Participants ({schedulingData.length})
                      </h3>
                    </div>

                    <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                      {schedulingData.length > 0 ? (
                        schedulingData.map((schedule) => (
                          <div
                            key={schedule.id}
                            style={{
                              padding: '16px',
                              borderBottom: '1px solid #F3F4F6',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '12px',
                              }}
                            >
                              <div>
                                <p
                                  style={{
                                    margin: '0 0 4px 0',
                                    fontWeight: '500',
                                  }}
                                >
                                  {schedule.email}
                                </p>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: '12px',
                                    color: '#6B7280',
                                  }}
                                >
                                  Started:{' '}
                                  {new Date(
                                    schedule.startDate,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={() =>
                                  removeSessionSchedule(
                                    schedule.id,
                                    schedule.email,
                                  )
                                }
                                style={{
                                  backgroundColor: '#FEF2F2',
                                  color: '#DC2626',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: '6px',
                              }}
                            >
                              {[
                                { num: 1, unlock: schedule.session1Unlock },
                                { num: 2, unlock: schedule.session2Unlock },
                                { num: 3, unlock: schedule.session3Unlock },
                                { num: 4, unlock: schedule.session4Unlock },
                                { num: 5, unlock: schedule.session5Unlock },
                              ].map((session) => {
                                const isUnlocked =
                                  getSessionStatus(session.unlock) ===
                                  'Unlocked';
                                const unlockDate = new Date(session.unlock);
                                return (
                                  <div
                                    key={session.num}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 6px',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '6px',
                                      backgroundColor: isUnlocked
                                        ? '#F0FDF4'
                                        : '#FEF2F2',
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        margin: '0 0 4px 0',
                                      }}
                                    >
                                      S{session.num}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: '9px',
                                        color: getSessionColor(session.unlock),
                                        fontWeight: '500',
                                        margin: '0 0 4px 0',
                                      }}
                                    >
                                      {getSessionStatus(session.unlock)}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: '9px',
                                        color: '#6B7280',
                                        margin: 0,
                                        lineHeight: '1.3',
                                      }}
                                    >
                                      {unlockDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                      <br />
                                      {unlockDate.toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true,
                                      })}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '48px 24px',
                            textAlign: 'center',
                            color: '#6B7280',
                          }}
                        >
                          <Calendar
                            size={48}
                            style={{ margin: '0 auto 16px', opacity: 0.3 }}
                          />
                          <p>No session schedules set</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'user-management' && (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '24px',
                  }}
                >
                  {/* Create User */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '24px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 20px 0',
                      }}
                    >
                      Create New User
                    </h3>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={newUserData.email}
                        onChange={(e) =>
                          setNewUserData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="user@example.com"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Password
                      </label>
                      <input
                        type="password"
                        value={newUserData.password}
                        onChange={(e) =>
                          setNewUserData((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        placeholder="Minimum 6 characters"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Patient ID (Optional)
                      </label>
                      <input
                        type="text"
                        value={newUserData.patientId}
                        onChange={(e) =>
                          setNewUserData((prev) => ({
                            ...prev,
                            patientId: e.target.value,
                          }))
                        }
                        placeholder="Enter patient ID"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        Notes (Optional)
                      </label>
                      <textarea
                        value={newUserData.notes}
                        onChange={(e) =>
                          setNewUserData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder="Add any notes about this user..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '13px',
                          resize: 'vertical',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'block',
                          marginBottom: '8px',
                        }}
                      >
                        User Role
                      </label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <label
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            border: '2px solid',
                            borderColor:
                              (newUserData.role || 'patient') === 'patient'
                                ? '#1B365D'
                                : '#E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor:
                              (newUserData.role || 'patient') === 'patient'
                                ? '#EFF6FF'
                                : 'white',
                          }}
                        >
                          <input
                            type="radio"
                            name="userRole"
                            value="patient"
                            checked={
                              (newUserData.role || 'patient') === 'patient'
                            }
                            onChange={(e) =>
                              setNewUserData((prev) => ({
                                ...prev,
                                role: e.target.value,
                              }))
                            }
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            Patient
                          </span>
                        </label>

                        <label
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            border: '2px solid',
                            borderColor:
                              newUserData.role === 'admin'
                                ? '#10B981'
                                : '#E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor:
                              newUserData.role === 'admin'
                                ? '#F0FDF4'
                                : 'white',
                          }}
                        >
                          <input
                            type="radio"
                            name="userRole"
                            value="admin"
                            checked={newUserData.role === 'admin'}
                            onChange={(e) =>
                              setNewUserData((prev) => ({
                                ...prev,
                                role: e.target.value,
                              }))
                            }
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            Admin
                          </span>
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        console.log(
                          '🔍 Form state when clicking Create:',
                          newUserData,
                        );
                        console.log('🔍 Role value:', newUserData.role);
                        console.log(
                          '🔍 Role is admin?',
                          newUserData.role === 'admin',
                        );

                        if (!newUserData.email || !newUserData.password) {
                          setError('Email and password are required');
                          return;
                        }

                        // Validate email format
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(newUserData.email)) {
                          alert(
                            '⚠️ Invalid email format. Please enter a valid email address (e.g., user@example.com)',
                          );
                          return;
                        }

                        if (newUserData.password.length < 6) {
                          alert(
                            '⚠️ Password must be at least 6 characters long',
                          );
                          return;
                        }

                        try {
                          setCreatingUser(true);
                          setError('');

                          const { data, error } =
                            await supabase.functions.invoke('manage-users', {
                              body: {
                                action: 'create',
                                email: newUserData.email,
                                password: newUserData.password,
                                patientId: newUserData.patientId,
                                notes: newUserData.notes,
                                role: newUserData.role || 'patient',
                              },
                            });

                          if (error) throw error;
                          if (data?.error) {
                            // Check for specific error types
                            if (
                              data.error.includes('duplicate key') ||
                              data.error.includes('already exists')
                            ) {
                              alert(
                                '⚠️ A user with this email already exists. Please use a different email address.',
                              );
                            } else if (data.error.includes('invalid format')) {
                              alert(
                                '⚠️ Invalid email format. Please enter a valid email address.',
                              );
                            } else {
                              alert('⚠️ Error: ' + data.error);
                            }
                            throw new Error(data.error);
                          }
                          if (!data?.success)
                            throw new Error(
                              'Function returned unexpected response',
                            );

                          // Clear form
                          setNewUserData({
                            email: '',
                            password: '',
                            patientId: '',
                            notes: '',
                            role: 'patient',
                          });

                          // Reload and show success
                          await loadDashboardData();
                          alert('✅ User created successfully!');
                        } catch (error) {
                          console.error('Error creating user:', error);
                          // Only show generic error if we haven't already shown a specific one
                          if (
                            !error.message.includes('duplicate') &&
                            !error.message.includes('invalid format')
                          ) {
                            setError('Error creating user: ' + error.message);
                          }
                        } finally {
                          setCreatingUser(false);
                        }
                      }}
                      disabled={
                        creatingUser ||
                        !newUserData.email ||
                        !newUserData.password
                      }
                      style={{
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: creatingUser ? 'not-allowed' : 'pointer',
                        opacity: creatingUser ? 0.5 : 1,
                        width: '100%',
                      }}
                    >
                      {creatingUser ? 'Creating...' : 'Create User'}
                    </button>
                  </div>

                  {/* Delete User */}
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '24px',
                    }}
                  >
                    <h3
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 12px 0',
                      }}
                    >
                      Delete User
                    </h3>

                    <div
                      style={{
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '20px',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#991B1B',
                          margin: 0,
                        }}
                      >
                        <strong>Warning:</strong> Deleting a user will
                        permanently remove all their data including sessions,
                        practice logs, and assessments. This action cannot be
                        undone.
                      </p>
                    </div>

                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {allParticipants.map((participant) => (
                        <div
                          key={participant.id}
                          style={{
                            padding: '12px',
                            borderBottom: '1px solid #F3F4F6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <p
                              style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                margin: '0 0 4px 0',
                              }}
                            >
                              {participant.email}
                            </p>
                            <p
                              style={{
                                fontSize: '12px',
                                color: '#6B7280',
                                margin: 0,
                              }}
                            >
                              ID:{' '}
                              {participant.patient_id ||
                                participant.id.substring(0, 8)}
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              if (
                                !confirm(
                                  `Are you sure you want to delete ${participant.email}? This action cannot be undone.`,
                                )
                              ) {
                                return;
                              }

                              try {
                                const { data, error } =
                                  await supabase.functions.invoke(
                                    'manage-users',
                                    {
                                      body: {
                                        action: 'delete',
                                        userId: participant.id,
                                      },
                                    },
                                  );

                                if (error) throw error;
                                if (data?.error) throw new Error(data.error);
                                if (!data?.success)
                                  throw new Error(
                                    'Function returned unexpected response',
                                  );

                                alert('User deleted successfully');
                                await loadDashboardData();
                              } catch (error) {
                                console.error('Error deleting user:', error);
                                setError(
                                  'Error deleting user: ' + error.message,
                                );
                              }
                            }}
                            style={{
                              backgroundColor: '#FEF2F2',
                              color: '#DC2626',
                              border: '1px solid #FECACA',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Data Export Tab */}
            {activeTab === 'data-export' && (
              <div
                style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                }}
              >
                <FileText
                  size={48}
                  color="#6B7280"
                  style={{ margin: '0 auto 16px' }}
                />
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: '0 0 8px 0',
                  }}
                >
                  Export Research Data
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '0 0 24px 0',
                  }}
                >
                  Download participant data (excludes test accounts)
                </p>
                <button
                  onClick={exportData}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor: '#1B365D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <Download size={16} />
                  Export as CSV
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Slide-out Panel */}
      {showHelpPanel && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 999,
            }}
            onClick={() => setShowHelpPanel(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              bottom: '20px',
              width: '800px',
              maxWidth: 'calc(90vw - 40px)',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              overflow: 'hidden',
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            <div style={{ height: '100%', overflow: 'auto' }}>
              {/* Panel Header */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #E5E7EB',
                  padding: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  zIndex: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      margin: '0 0 4px 0',
                    }}
                  >
                    Help & Documentation
                  </h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                    Complete guide for the Researcher Dashboard
                  </p>
                </div>
                <button
                  onClick={() => setShowHelpPanel(false)}
                  style={{
                    padding: '8px',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={20} color="#6B7280" />
                </button>
              </div>
              {/* Search Bar */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#F9FAFB',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Search
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF',
                    }}
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    value={helpSearchTerm}
                    onChange={(e) => setHelpSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 40px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Panel Content */}
              <div style={{ padding: '24px' }}>
                {/* Featured Quick Start */}
                <div
                  style={{
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#DBEAFE',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <UserCheck size={24} color="#1B365D" />
                    </div>
                    <div>
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          margin: '0 0 4px 0',
                          color: '#1B365D',
                        }}
                      >
                        Onboarding New Participants
                      </h4>
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#1E40AF',
                          margin: 0,
                        }}
                      >
                        Follow these steps to set up a new participant
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#1B365D',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        1
                      </div>
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1B365D',
                            margin: '0 0 4px 0',
                          }}
                        >
                          Create User Account
                        </p>
                        <p
                          style={{
                            fontSize: '13px',
                            color: '#1E40AF',
                            margin: 0,
                            lineHeight: '1.5',
                          }}
                        >
                          Go to User Management tab, enter email and password,
                          optionally assign a patient ID
                        </p>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#1B365D',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        2
                      </div>
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1B365D',
                            margin: '0 0 4px 0',
                          }}
                        >
                          Set Session Schedule
                        </p>
                        <p
                          style={{
                            fontSize: '13px',
                            color: '#1E40AF',
                            margin: 0,
                            lineHeight: '1.5',
                          }}
                        >
                          In Session Scheduling, choose start date and time -
                          sessions will unlock automatically every 7 days
                        </p>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#1B365D',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        3
                      </div>
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1B365D',
                            margin: '0 0 4px 0',
                          }}
                        >
                          Configure Assessment Links
                        </p>
                        <p
                          style={{
                            fontSize: '13px',
                            color: '#1E40AF',
                            margin: 0,
                            lineHeight: '1.5',
                          }}
                        >
                          In Assessment Links, add REDCap URLs for each
                          session's pre-session assessments
                        </p>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#1B365D',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        4
                      </div>
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1B365D',
                            margin: '0 0 4px 0',
                          }}
                        >
                          Monitor Progress
                        </p>
                        <p
                          style={{
                            fontSize: '13px',
                            color: '#1E40AF',
                            margin: 0,
                            lineHeight: '1.5',
                          }}
                        >
                          Track participant engagement in the Participants tab
                          and use Activity Monitor to identify those needing
                          follow-up
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Topics Grid */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <h4
                      style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}
                    >
                      Topics
                    </h4>
                    {helpSearchTerm && (
                      <button
                        onClick={() => setHelpSearchTerm('')}
                        style={{
                          fontSize: '13px',
                          color: '#6B7280',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Clear search
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gap: '12px' }}>
                    {[
                      {
                        title: 'Managing Participants',
                        description:
                          'View details, categorize users, and track progress',
                        icon: Users,
                        color: '#1B365D',
                        bgColor: '#EFF6FF',
                        id: 'participants',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: Participant table with View button
                              and three-dot menu highlighted
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 12px 0',
                              }}
                            >
                              Categories
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li style={{ marginBottom: '6px' }}>
                                <strong>Active:</strong> Regular participants
                              </li>
                              <li style={{ marginBottom: '6px' }}>
                                <strong>Pending:</strong> Awaiting patient ID
                              </li>
                              <li style={{ marginBottom: '6px' }}>
                                <strong>Test:</strong> Excluded from stats
                              </li>
                              <li style={{ marginBottom: '6px' }}>
                                <strong>Admin:</strong> Auto-categorized by role
                              </li>
                            </ul>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '16px 0 8px 0',
                              }}
                            >
                              Moving Participants
                            </h5>
                            <ol
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Find participant in table</li>
                              <li>Click three-dot menu (⋮)</li>
                              <li>Select category</li>
                            </ol>
                          </>
                        ),
                      },
                      {
                        title: 'Activity Monitoring',
                        description:
                          'Track engagement and identify inactive participants',
                        icon: Activity,
                        color: '#10B981',
                        bgColor: '#F0FDF4',
                        id: 'activity',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: Activity Monitor tab showing Active
                              Today, Need Attention, and Inactive sections
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 12px 0',
                              }}
                            >
                              Status Colors
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                              }}
                            >
                              <li
                                style={{
                                  marginBottom: '6px',
                                  color: '#10B981',
                                }}
                              >
                                <strong>Green (Active):</strong> ≤ 3 days
                              </li>
                              <li
                                style={{
                                  marginBottom: '6px',
                                  color: '#F59E0B',
                                }}
                              >
                                <strong>Orange (Low):</strong> 3-7 days
                              </li>
                              <li
                                style={{
                                  marginBottom: '6px',
                                  color: '#EF4444',
                                }}
                              >
                                <strong>Red (Inactive):</strong> 7+ days
                              </li>
                            </ul>
                            <p
                              style={{
                                fontSize: '13px',
                                color: '#374151',
                                margin: '12px 0 0 0',
                                lineHeight: '1.6',
                              }}
                            >
                              Use Activity Monitor tab to identify participants
                              needing follow-up.
                            </p>
                          </>
                        ),
                      },
                      {
                        title: 'Assessment Links',
                        description:
                          'Configure REDCap URLs for pre/post assessments',
                        icon: FileText,
                        color: '#F59E0B',
                        bgColor: '#FEF3C7',
                        id: 'assessments',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: Assessment Links tab with session
                              buttons and URL input fields
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 8px 0',
                              }}
                            >
                              Setup Steps
                            </h5>
                            <ol
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Select session (1-5)</li>
                              <li>Search participant by email</li>
                              <li>Enter REDCap URLs</li>
                              <li>Click Save Links</li>
                            </ol>
                            <div
                              style={{
                                backgroundColor: '#FEF3C7',
                                border: '1px solid #FDE68A',
                                borderRadius: '6px',
                                padding: '10px',
                                marginTop: '12px',
                              }}
                            >
                              <p
                                style={{
                                  fontSize: '12px',
                                  color: '#92400E',
                                  margin: 0,
                                }}
                              >
                                <strong>Note:</strong> Session 1 = post only.
                                Sessions 2-5 = pre + post.
                              </p>
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '16px 0 8px 0',
                              }}
                            >
                              Assessment Boxes
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li style={{ marginBottom: '4px' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#10B981',
                                    marginRight: '6px',
                                  }}
                                ></span>
                                Green = Pre completed
                              </li>
                              {/* <li style={{ marginBottom: '4px' }}>
                  <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#3B82F6', marginRight: '6px' }}></span>
                  Blue = Post completed
                </li> */}
                            </ul>
                          </>
                        ),
                      },
                      {
                        title: 'Session Scheduling',
                        description:
                          'Set unlock times and manage participant timelines',
                        icon: Calendar,
                        color: '#7C3AED',
                        bgColor: '#F3E8FF',
                        id: 'scheduling',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: Session Scheduling with date
                              picker, time picker, and unlock time display
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 8px 0',
                              }}
                            >
                              Creating Schedule
                            </h5>
                            <ol
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Select participant</li>
                              <li>Choose Session 1 start date</li>
                              <li>Set start time</li>
                              <li>Add notes (optional)</li>
                              <li>Click Save</li>
                            </ol>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '16px 0 8px 0',
                              }}
                            >
                              Unlock Schedule
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Session 1: Start date</li>
                              <li>Sessions 2-5: 7 days apart</li>
                              <li>All unlock at same time daily</li>
                            </ul>
                          </>
                        ),
                      },
                      {
                        title: 'User Management',
                        description:
                          'Create participants, assign roles, and delete users',
                        icon: UserCheck,
                        color: '#EC4899',
                        bgColor: '#FDF2F8',
                        id: 'users',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: User Management tab with Create
                              User form showing role selection
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 8px 0',
                              }}
                            >
                              Creating Users
                            </h5>
                            <ol
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Enter email & password</li>
                              <li>Add patient ID (optional)</li>
                              <li>Select role (Patient or Admin)</li>
                              <li>Click Create User</li>
                            </ol>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '16px 0 8px 0',
                              }}
                            >
                              Setting Admin Access
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>During creation: Select "Admin" role</li>
                              <li>After creation: Toggle in View modal</li>
                            </ul>
                            <div
                              style={{
                                backgroundColor: '#FEF2F2',
                                border: '1px solid #FECACA',
                                borderRadius: '6px',
                                padding: '10px',
                                marginTop: '12px',
                              }}
                            >
                              <p
                                style={{
                                  fontSize: '12px',
                                  color: '#991B1B',
                                  margin: 0,
                                }}
                              >
                                <strong>Warning:</strong> Deleting removes all
                                user data permanently.
                              </p>
                            </div>
                          </>
                        ),
                      },
                      {
                        title: 'Data Export',
                        description:
                          'Download participant data as CSV for analysis',
                        icon: Download,
                        color: '#3B82F6',
                        bgColor: '#EFF6FF',
                        id: 'export',
                        content: (
                          <>
                            <div
                              style={{
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '40px',
                                marginBottom: '16px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontSize: '13px',
                              }}
                            >
                              📸 Screenshot: Data Export tab with Export as CSV
                              button
                            </div>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 8px 0',
                              }}
                            >
                              Exporting
                            </h5>
                            <ol
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Go to Data Export tab</li>
                              <li>Click "Export as CSV"</li>
                              <li>File downloads automatically</li>
                            </ol>
                            <h5
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '16px 0 8px 0',
                              }}
                            >
                              CSV Contents
                            </h5>
                            <ul
                              style={{
                                marginLeft: '20px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#374151',
                              }}
                            >
                              <li>Email & progress</li>
                              <li>Sessions completed</li>
                              <li>Practice minutes</li>
                              <li>Assessment counts</li>
                            </ul>
                            <div
                              style={{
                                backgroundColor: '#DBEAFE',
                                border: '1px solid #93C5FD',
                                borderRadius: '6px',
                                padding: '10px',
                                marginTop: '12px',
                              }}
                            >
                              <p
                                style={{
                                  fontSize: '12px',
                                  color: '#1E40AF',
                                  margin: 0,
                                }}
                              >
                                Test accounts excluded from exports.
                              </p>
                            </div>
                          </>
                        ),
                      },
                    ]
                      .filter(
                        (topic) =>
                          !helpSearchTerm ||
                          topic.title
                            .toLowerCase()
                            .includes(helpSearchTerm.toLowerCase()) ||
                          topic.description
                            .toLowerCase()
                            .includes(helpSearchTerm.toLowerCase()),
                      )
                      .map((topic) => {
                        const Icon = topic.icon;
                        const expanded = expandedHelpTopic === topic.id;

                        return (
                          <div
                            key={topic.id}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              transition: 'all 0.2s',
                            }}
                          >
                            <button
                              onClick={() =>
                                setExpandedHelpTopic(expanded ? null : topic.id)
                              }
                              style={{
                                width: '100%',
                                padding: '16px',
                                backgroundColor: expanded
                                  ? topic.bgColor
                                  : 'white',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'background-color 0.2s',
                              }}
                            >
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: topic.bgColor,
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Icon size={20} color={topic.color} />
                              </div>
                              <div style={{ flex: 1, textAlign: 'left' }}>
                                <p
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    margin: '0 0 2px 0',
                                    color: '#111827',
                                  }}
                                >
                                  {topic.title}
                                </p>
                                <p
                                  style={{
                                    fontSize: '12px',
                                    color: '#6B7280',
                                    margin: 0,
                                  }}
                                >
                                  {topic.description}
                                </p>
                              </div>
                              <div
                                style={{
                                  transform: expanded
                                    ? 'rotate(90deg)'
                                    : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}
                              >
                                <CheckCircle size={16} color="#9CA3AF" />
                              </div>
                            </button>
                            {expanded && (
                              <div
                                style={{
                                  padding: '16px 20px',
                                  borderTop: '1px solid #E5E7EB',
                                  backgroundColor: '#F9FAFB',
                                }}
                              >
                                {topic.content}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* FAQ Section - Always visible at bottom */}
                <div
                  style={{
                    padding: '24px',
                    borderTop: '1px solid #E5E7EB',
                    backgroundColor: '#F9FAFB',
                  }}
                >
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      margin: '0 0 16px 0',
                    }}
                  >
                    Questions
                  </h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {[
                      {
                        q: 'What does the "Intro" badge mean?',
                        a: 'Participant completed Introduction Session video.',
                      },
                      {
                        q: "Why can't a participant access sessions?",
                        a: 'Check if schedule is set and unlock time has passed.',
                      },
                      {
                        q: 'Can I change a schedule after setting it?',
                        a: 'Yes! Select the participant again and enter new date/time.',
                      },
                      {
                        q: 'How is Assessment Rate calculated?',
                        a: '(Completed assessments) / (Expected assessments) × 100',
                      },
                    ]
                      .filter(
                        (faq) =>
                          !helpSearchTerm ||
                          faq.q
                            .toLowerCase()
                            .includes(helpSearchTerm.toLowerCase()) ||
                          faq.a
                            .toLowerCase()
                            .includes(helpSearchTerm.toLowerCase()),
                      )
                      .map((faq, idx) => (
                        <div
                          key={idx}
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            padding: '12px',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#1B365D',
                              margin: '0 0 4px 0',
                            }}
                          >
                            {faq.q}
                          </p>
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#6B7280',
                              margin: 0,
                              lineHeight: '1.5',
                            }}
                          >
                            {faq.a}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes slideIn {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }
  `,
        }}
      />
    </div>
  );
};

export default ResearcherDashboard;